// v5.0 — Sélecteur de produits : le commerçant choisit les produits de SA boutique,
// l'IA améliore automatiquement les photos pour le catalogue.
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trash2, Sparkles, Plus, Store } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` } });
const abs = (u) => (u && !u.startsWith("http") ? `${BACKEND_URL}${u}` : u);
const fmt = (n) => `${Number(n).toLocaleString("fr-FR")} FCFA`;

const Thumb = ({ photo, emoji }) => (
  <div className="w-11 h-11 rounded-xl overflow-hidden bg-white/[0.06] flex items-center justify-center text-xl shrink-0">
    {photo ? <img src={abs(photo)} alt="" className="w-full h-full object-cover" /> : (emoji || "🛍️")}
  </div>
);

export const ProductEditor = ({ catalog, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/v3/shop/products`, auth()).then((r) => setInventory(r.data?.products || [])).catch(() => setInventory([]));
    axios.get(`${BACKEND_URL}/api/v3/catalogs/${catalog.catalogId}/products`, auth()).then((r) => setSelected(r.data?.products || [])).catch(() => setSelected([]));
  }, [catalog.catalogId]);
  useEffect(load, [load]);

  // Poll tant que des photos sont en cours d'amélioration IA
  useEffect(() => {
    const pending = (selected || []).some((p) => p.enhance_status === "pending" || p.enhance_status === "running");
    if (pending && !pollRef.current) {
      pollRef.current = setInterval(() => {
        axios.get(`${BACKEND_URL}/api/v3/catalogs/${catalog.catalogId}/products`, auth()).then((r) => setSelected(r.data?.products || [])).catch(() => {});
      }, 5000);
    }
    if (!pending && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [selected, catalog.catalogId]);

  const add = async (productId) => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/v3/catalogs/${catalog.catalogId}/products/select`, { product_ids: [productId] }, auth());
      toast.success("Ajouté ✨ La photo est en cours d'amélioration IA");
      load(); onChanged?.();
    } catch { toast.error("Erreur — réessaie"); }
    finally { setBusy(false); }
  };

  const remove = async (productId) => {
    await axios.delete(`${BACKEND_URL}/api/v3/catalogs/${catalog.catalogId}/products/${productId}`, auth()).catch(() => {});
    toast.success("Retiré du catalogue");
    load(); onChanged?.();
  };

  const toggleAvailable = async (p) => {
    await axios.put(`${BACKEND_URL}/api/v3/catalogs/${catalog.catalogId}/products/${p.productId}`, { available: p.available === false }, auth()).catch(() => {});
    load();
  };

  const inCatalog = new Set((selected || []).map((p) => p.source_product_id));
  const available = (inventory || []).filter((p) => !inCatalog.has(p.product_id));

  return (
    <div className="fixed inset-0 z-50 bg-[#0a0a0d] overflow-y-auto" data-testid="product-editor">
      <div className="sticky top-0 z-10 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={onClose} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 text-white/80" data-testid="pe-back-btn"><ArrowLeft size={19} /></button>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold truncate">📦 Produits — {catalog.title}</div>
            <div className="text-[10.5px] text-white/45">Choisis les produits de ta boutique · l'IA améliore les photos automatiquement</div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-6">
        {/* ── Dans ce catalogue ── */}
        <div>
          <div className="text-[11.5px] font-extrabold text-amber-300 uppercase tracking-wider mb-2">Dans ce catalogue ({selected?.length ?? "…"})</div>
          {selected === null ? (
            <div className="h-16 rounded-2xl bg-white/[0.05] animate-pulse" />
          ) : selected.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 py-6 text-center text-[12px] text-white/45" data-testid="pe-empty">
              Aucun produit — sélectionne-les depuis ta boutique ci-dessous 👇
            </div>
          ) : (
            <div className="space-y-2.5">
              {selected.map((p) => (
                <div key={p.productId} className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${p.available === false ? "bg-white/[0.02] border-white/6 opacity-60" : "bg-white/[0.04] border-white/10"}`} data-testid={`pe-item-${p.productId}`}>
                  <Thumb photo={p.photo} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold truncate">{p.name}</div>
                    <div className="text-[11px] text-amber-300 font-extrabold flex items-center gap-2">
                      {fmt(p.price_fcfa)}
                      {(p.enhance_status === "pending" || p.enhance_status === "running") && (
                        <span className="text-[9.5px] font-bold text-white/60 flex items-center gap-1" data-testid={`pe-enhancing-${p.productId}`}><Sparkles size={10} className="text-amber-300 animate-pulse" /> Amélioration IA…</span>
                      )}
                      {p.enhance_status === "done" && <span className="text-[9.5px] font-bold text-emerald-400">✨ Photo améliorée</span>}
                      {p.available === false && <span className="text-red-400/90 font-semibold">Épuisé</span>}
                    </div>
                  </div>
                  <button onClick={() => toggleAvailable(p)} data-testid={`pe-toggle-${p.productId}`}
                    className={`text-[10px] font-bold px-2 py-1.5 rounded-lg border ${p.available === false ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-white/55"}`}>
                    {p.available === false ? "Remettre" : "Épuisé"}
                  </button>
                  <button onClick={() => remove(p.productId)} className="p-2 rounded-full hover:bg-white/10 text-red-400/80" data-testid={`pe-delete-${p.productId}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Ma boutique (inventaire) ── */}
        <div>
          <div className="text-[11.5px] font-extrabold text-white/60 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Store size={12} /> Ma boutique ({inventory === null ? "…" : available.length})</div>
          {inventory === null ? (
            <div className="h-16 rounded-2xl bg-white/[0.05] animate-pulse" />
          ) : inventory.length === 0 ? (
            <div className="rounded-2xl border border-amber-400/25 bg-white/[0.04] p-4 text-center space-y-2" data-testid="pe-no-inventory">
              <div className="text-2xl">🏪</div>
              <div className="text-[13px] font-bold">Ta boutique n'a pas encore de produits</div>
              <div className="text-[11.5px] text-white/50">Ajoute d'abord tes produits (nom, prix, photo) dans « Ma boutique » — ils seront ensuite sélectionnables ici.</div>
              <button onClick={() => navigate("/mon-compte?tab=pro")} data-testid="pe-goto-boutique" className="rounded-xl bg-amber-400 text-black font-bold text-[12px] px-4 py-2 mt-1">Gérer ma boutique</button>
            </div>
          ) : available.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 py-5 text-center text-[12px] text-white/45">Tous tes produits sont déjà dans ce catalogue ✅</div>
          ) : (
            <div className="space-y-2.5">
              {available.map((p) => (
                <div key={p.product_id} className="flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/10 px-3 py-2.5" data-testid={`pe-inv-${p.product_id}`}>
                  <Thumb photo={p.photo} emoji={p.emoji} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-bold truncate">{p.name}</div>
                    <div className="text-[11px] text-white/55 font-semibold">{fmt(p.price_fcfa)}</div>
                  </div>
                  <button onClick={() => add(p.product_id)} disabled={busy} data-testid={`pe-add-${p.product_id}`}
                    className="rounded-xl bg-amber-400 text-black font-bold text-[11px] px-3 py-2 flex items-center gap-1 disabled:opacity-50">
                    <Plus size={13} /> Ajouter
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
